//EditJob.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchJobById, updateJob } from '../services/jobService';
import { toast } from 'react-toastify';

// ErrorBoundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return <h2>Something went wrong. Please try again later.</h2>;
    }
    return this.props.children;
  }
}

export default function EditJobWrapper() {
  return (
    <ErrorBoundary>
      <EditJob />
    </ErrorBoundary>
  );
}

function EditJob() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  const ensureAuthenticated = async () => {
    try {
      const refreshed = await window.keycloak.updateToken(30);
      if (refreshed) {
        console.log('Token refreshed');
      }
    } catch (error) {
      toast.error('Session expired. Redirecting to login...');
      window.keycloak.login({ redirectUri: window.location.href });
      throw error;
    }
  };

  useEffect(() => {
    const loadJob = async () => {
      try {
        await ensureAuthenticated();
        const data = await fetchJobById(id);

        // Format ISO date strings to YYYY-MM-DD for input fields
        data.postedDate = data.postedDate?.split('T')[0] || '';
        data.closingDate = data.closingDate?.split('T')[0] || '';

        setJob(data);
      } catch (err) {
        if (err.response?.status === 401) {
          toast.error('Unauthorized. Please login again.');
          window.keycloak.login({ redirectUri: window.location.href });
        } else {
          toast.error('Failed to load job details');
        }
      } finally {
        setLoading(false);
      }
    };
    loadJob();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setJob((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await ensureAuthenticated();
      await updateJob(id, job);
      toast.success('Job updated successfully');
      navigate('/');
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        window.keycloak.login({ redirectUri: window.location.href });
      } else {
        toast.error('Update failed');
      }
    }
  };

  if (loading || !job) {
    return (
      <div style={{ padding: '20px', maxWidth: '700px', margin: 'auto' }}>
        <h2>Loading Job Details...</h2>
        <div style={styles.skeleton}></div>
        <div style={styles.skeleton}></div>
        <div style={styles.skeleton}></div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '700px', margin: 'auto' }}>
      <button onClick={() => navigate('/')} style={styles.backButton}>← Back to Home</button>
      <h2>Edit Job Posting</h2>

      {job.imgUrl && (
        <div style={{ marginBottom: '16px' }}>
          <img src={job.imgUrl} alt="Job" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }} />
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {renderInput('title', 'Job Title')}
        {renderInput('companyName', 'Company Name')}
        {renderInput('companyWebsite', 'Company Website')}
        {renderInput('location', 'Location')}
        {renderInput('salaryRange', 'Salary Range')}
        {renderInput('jobType', 'Job Type')}
        {renderInput('educationLevel', 'Education Level')}
        {renderInput('postedDate', 'Posted Date', 'date')}
        {renderInput('closingDate', 'Closing Date', 'date')}
        {renderInput('jobUrl', 'Job URL')}
        {renderInput('courseName', 'Course Name')}
        {renderInput('experienceLevel', 'Experience Level')}
        {renderInput('organizationCategory', 'Category')}
        {renderInput('imgUrl', 'Image URL')}
        {renderTextarea('description', 'Description')}
        {renderTextarea('companyDetails', 'Company Details')}
        {renderTextarea('jobResponsibilities', 'Responsibilities')}
        {renderTextarea('requirements', 'Requirements')}
        {renderTextarea('applicationMethod', 'Application Method')}

        <button type="submit" style={styles.button}>Update Job</button>
      </form>
    </div>
  );

  function renderInput(name, label, type = 'text') {
    return (
      <div style={styles.fieldGroup}>
        <label htmlFor={name}>{label}</label>
        <input
          id={name}
          name={name}
          type={type}
          value={job[name] || ''}
          onChange={handleChange}
          style={styles.input}
        />
      </div>
    );
  }

  function renderTextarea(name, label) {
    return (
      <div style={styles.fieldGroup}>
        <label htmlFor={name}>{label}</label>
        <textarea
          id={name}
          name={name}
          value={job[name] || ''}
          onChange={handleChange}
          rows="3"
          style={styles.textarea}
        />
      </div>
    );
  }
}

const styles = {
  input: {
    width: '100%',
    padding: '8px',
    marginTop: '4px',
    borderRadius: '4px',
    border: '1px solid #ccc',
  },
  textarea: {
    width: '100%',
    padding: '8px',
    marginTop: '4px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    resize: 'vertical',
  },
  button: {
    marginTop: '10px',
    padding: '10px 16px',
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  backButton: {
    marginBottom: '16px',
    padding: '6px 12px',
    backgroundColor: '#9e9e9e',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  fieldGroup: {
    marginBottom: '12px'
  },
  skeleton: {
    backgroundColor: '#eee',
    height: '20px',
    borderRadius: '4px',
    margin: '10px 0',
    animation: 'pulse 1.5s infinite',
  },
};
